import { useState, useEffect, useRef, KeyboardEvent, useCallback, ChangeEvent } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Question } from '../types/interaction'
import { playNotificationSound } from '../utils/sounds'
import { useTheme } from '../contexts/ThemeContext'

interface InteractionPanelProps {
  questions: Question[]
  onAnswer: (questionId: string, value: string | string[]) => void
  notificationSound?: 'none' | 'chime' | 'bell' | 'subtle'
  /** The project on screen. A question from any other project is still
   * shown, and says which project asked it (L3-3). */
  viewedProject?: string
}

export function InteractionPanel({ questions, onAnswer, notificationSound = 'chime', viewedProject }: InteractionPanelProps) {
  const { theme } = useTheme()
  const [showWarning, setShowWarning] = useState(false)
  const [isTextMode, setIsTextMode] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [textValue, setTextValue] = useState('')
  const [multiSelectValues, setMultiSelectValues] = useState<Set<string>>(new Set())
  const textInputRef = useRef<HTMLTextAreaElement>(null)
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([])

  // Always show the first (and only) question
  const question = questions[0]

  const isLurkerAlert = question?.workerName?.toLowerCase().includes('lurker') ||
    question?.workerName?.startsWith('Nyarlathotep') ||
    question?.workerName?.startsWith('Yithian') ||
    question?.workerName?.startsWith('Ithaqua') ||
    question?.workerName?.startsWith('Tsathoggua')

  // All hooks must be called unconditionally before any early returns
  useEffect(() => {
    if (!question) return

    const timer = setTimeout(() => {
      setShowWarning(true)
    }, 5 * 60 * 1000)

    return () => clearTimeout(timer)
  }, [question?.id])

  // Reset state when question changes
  useEffect(() => {
    if (!question) return

    setIsTextMode(false)
    setSelectedIndex(0)
    setTextValue('')
    setShowWarning(false)
    setMultiSelectValues(new Set())
  }, [question?.id])

  // Reset refs when question changes
  useEffect(() => {
    buttonRefs.current = []
  }, [question?.id])

  // Request notification permission and show notification if tab is hidden
  useEffect(() => {
    if (!question) return

    const requestNotificationPermission = async () => {
      if (typeof window !== 'undefined' && 'Notification' in window) {
        if (Notification.permission === 'default') {
          await Notification.requestPermission()
        }

        if (Notification.permission === 'granted' && document.hidden) {
          new Notification(`Star Forge - Question from ${question.workerName}`, {
            body: question.prompt,
            icon: '/favicon.ico',
            tag: question.id,
          })
        }
      }
    }

    requestNotificationPermission()
  }, [question?.id, question?.workerName, question?.prompt])

  // Play audio notification
  useEffect(() => {
    if (!question) return

    if (notificationSound && notificationSound !== 'none') {
      playNotificationSound(notificationSound)
    }
  }, [question?.id, notificationSound])

  // Auto-expand textarea height based on content
  const adjustTextareaHeight = useCallback((textarea: HTMLTextAreaElement | null) => {
    if (!textarea) return
    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto'
    // Calculate new height, capped at max-height (handled by CSS)
    const newHeight = Math.min(textarea.scrollHeight, 300) // 300px max height
    textarea.style.height = `${newHeight}px`
  }, [])

  // Early return after all hooks have been called
  if (!question || questions.length === 0) {
    return null
  }

  const fromAnotherProject = Boolean(question.projectName && viewedProject && question.projectName !== viewedProject)

  const handleChoiceClick = (value: string) => {
    onAnswer(question.id, value)
  }

  const handleConfirmClick = (value: string) => {
    onAnswer(question.id, value)
  }

  const handleTextSubmit = () => {
    if (textValue.trim()) {
      onAnswer(question.id, textValue.trim())
      setTextValue('')
    }
  }

  const handleMultiSelectToggle = (value: string) => {
    setMultiSelectValues((prev) => {
      const next = new Set(prev)
      if (next.has(value)) {
        next.delete(value)
      } else {
        next.add(value)
      }
      return next
    })
  }

  const handleMultiSelectSubmit = () => {
    if (multiSelectValues.size > 0) {
      onAnswer(question.id, Array.from(multiSelectValues))
    }
  }

  const handleOptionKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (question.type !== 'choice' || !question.options) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      const nextIndex = (index + 1) % question.options.length
      buttonRefs.current[nextIndex]?.focus()
      setSelectedIndex(nextIndex)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      const prevIndex = (index - 1 + question.options.length) % question.options.length
      buttonRefs.current[prevIndex]?.focus()
      setSelectedIndex(prevIndex)
    } else if (e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault()
      setIsTextMode(true)
      const option = question.options?.[index]
      if (option) {
        setTextValue(option.value)
      }
      setTimeout(() => textInputRef.current?.focus(), 0)
    }
  }

  const handleTextKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Tab' && e.shiftKey) {
      e.preventDefault()
      setIsTextMode(false)
      setTimeout(() => buttonRefs.current[selectedIndex]?.focus(), 0)
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleTextSubmit()
    }
    // Shift+Enter allows normal newline insertion
  }

  const handleTextChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setTextValue(e.target.value)
    adjustTextareaHeight(e.target)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm overflow-y-auto p-4">
      {/* Modal overlay - click backdrop to dismiss (optional - currently disabled) */}
      <div className="relative w-full max-w-3xl lg:max-w-4xl xl:max-w-5xl my-auto">
        {/* Modal content with themed panel */}
        <div className={`rounded-xl border ${isLurkerAlert ? 'border-amber-500/40' : 'border-cosmic-nebula/30'} bg-cosmic-depth/80 shadow-2xl overflow-hidden themed-panel max-h-[calc(100dvh-2rem)] overflow-y-auto ${theme === 'glass-morphism' ? 'backdrop-blur-xl' : ''}`}>
          {/* Warning banner */}
          {showWarning && (
            <div className="bg-amber-500/20 border-b border-amber-500/30 px-4 py-2 text-amber-300 text-sm flex items-center gap-2">
              <span className="text-amber-400">!</span>
              <span>URGENT: This question has been waiting for 5 minutes</span>
            </div>
          )}

          {/* A question from a project other than the one on screen */}
          {fromAnotherProject && (
            <div data-testid="other-project-notice" className="bg-amber-500/20 border-b border-amber-500/30 px-4 py-2 text-amber-300 text-sm">
              This question is from <span className="font-semibold">{question.projectName}</span>, not {viewedProject}. Answering here answers it.
            </div>
          )}

          {/* Modal body */}
          <div className="p-6">
            {/* Worker badge + dismiss */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${isLurkerAlert ? 'bg-amber-500' : 'bg-cosmic-nebula'} animate-pulse`} />
                <span className={`text-sm font-medium ${isLurkerAlert ? 'text-amber-400' : 'text-cosmic-nebula-light'}`}>
                  {isLurkerAlert ? `System Alert — ${question.workerName}` : question.workerName}
                </span>
                {question.projectName && (
                  <span data-testid="question-project" className="text-xs text-gray-400">in {question.projectName}</span>
                )}
                {questions.length > 1 && (
                  <span className="text-xs text-gray-500">({questions.length - 1} more waiting)</span>
                )}
              </div>
              <button
                onClick={() => onAnswer(question.id, '__DISMISSED__')}
                className="text-xs text-gray-500 hover:text-gray-300 transition-colors px-2 py-1 rounded hover:bg-cosmic-surface/50"
                title="Dismiss — the agent will wrap up immediately"
              >
                Dismiss
              </button>
            </div>

            {/* Dialogue/reasoning text - rendered as markdown */}
            {question.dialogue && (
              <div className="mb-4 p-3 rounded-lg bg-cosmic-void/50 border border-cosmic-border text-sm text-gray-300 leading-relaxed max-h-64 overflow-y-auto markdown-content">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {question.dialogue}
                </ReactMarkdown>
              </div>
            )}

            {/* Question prompt */}
            <div className="text-lg text-white font-medium mb-6 markdown-content">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {question.prompt}
              </ReactMarkdown>
            </div>

            {/* Choice options */}
            {question.type === 'choice' && question.options && (
              <>
                {!isTextMode ? (
                  <>
                    <div className="space-y-2">
                      {question.options.map((option, index) => (
                        <button
                          key={option.value}
                          ref={(el) => (buttonRefs.current[index] = el)}
                          className="w-full px-4 py-3 text-left rounded-lg border border-cosmic-border bg-cosmic-surface/50 text-white hover:bg-cosmic-nebula/20 hover:border-cosmic-nebula/50 transition-colors focus:outline-none focus:ring-2 focus:ring-cosmic-nebula/50"
                          onClick={() => handleChoiceClick(option.value)}
                          onKeyDown={(e) => handleOptionKeyDown(e, index)}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                    <button
                      className="text-xs text-gray-400 mt-4 mx-auto block hover:text-cosmic-nebula-light transition-colors"
                      onClick={() => {
                        setIsTextMode(true)
                        const option = question.options?.[selectedIndex]
                        if (option) setTextValue(option.value)
                        setTimeout(() => textInputRef.current?.focus(), 0)
                      }}
                    >
                      Type a custom response
                    </button>
                  </>
                ) : (
                  <>
                    <div className="flex flex-col gap-2">
                      <textarea
                        ref={textInputRef}
                        value={textValue}
                        onChange={handleTextChange}
                        onKeyDown={handleTextKeyDown}
                        placeholder="Type your answer..."
                        rows={2}
                        className="w-full px-4 py-3 rounded-lg border border-cosmic-border bg-cosmic-void/50 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cosmic-nebula/50 focus:border-cosmic-nebula/50 resize-none overflow-y-auto"
                        style={{ minHeight: '60px', maxHeight: '300px' }}
                      />
                      <div className="flex justify-end">
                        <button
                          onClick={handleTextSubmit}
                          className="px-4 py-3 rounded-lg bg-cosmic-nebula text-white font-medium hover:bg-cosmic-nebula/80 transition-colors"
                        >
                          Submit
                        </button>
                      </div>
                    </div>
                    <div className="flex justify-between items-center mt-2">
                      <button
                        className="text-xs text-gray-400 hover:text-cosmic-nebula-light transition-colors"
                        onClick={() => {
                          setIsTextMode(false)
                          setTimeout(() => buttonRefs.current[selectedIndex]?.focus(), 0)
                        }}
                      >
                        Back to options
                      </button>
                      <span className="text-xs text-gray-500">Shift+Enter for new line</span>
                    </div>
                  </>
                )}
              </>
            )}

            {/* Text input */}
            {question.type === 'text' && (
              <div className="flex flex-col gap-2">
                <textarea
                  value={textValue}
                  onChange={handleTextChange}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault()
                      handleTextSubmit()
                    }
                  }}
                  placeholder="Type your answer..."
                  rows={2}
                  className="w-full px-4 py-3 rounded-lg border border-cosmic-border bg-cosmic-void/50 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cosmic-nebula/50 focus:border-cosmic-nebula/50 resize-none overflow-y-auto"
                  style={{ minHeight: '60px', maxHeight: '300px' }}
                  autoFocus
                />
                <div className="flex justify-between items-center">
                  <p className="text-xs text-gray-500">
                    Shift+Enter for new line
                  </p>
                  <button
                    onClick={handleTextSubmit}
                    className="px-4 py-3 rounded-lg bg-cosmic-nebula text-white font-medium hover:bg-cosmic-nebula/80 transition-colors"
                  >
                    Submit
                  </button>
                </div>
              </div>
            )}

            {/* Confirm buttons */}
            {question.type === 'confirm' && (
              <div className="flex gap-3">
                <button
                  onClick={() => handleConfirmClick('yes')}
                  className="flex-1 px-4 py-3 rounded-lg bg-emerald-600 text-white font-medium hover:bg-emerald-500 transition-colors"
                >
                  Yes
                </button>
                <button
                  onClick={() => handleConfirmClick('no')}
                  className="flex-1 px-4 py-3 rounded-lg bg-cosmic-surface border border-cosmic-border text-white font-medium hover:bg-cosmic-surface/80 transition-colors"
                >
                  No
                </button>
              </div>
            )}

            {/* Multi-select checkboxes */}
            {question.type === 'multi-select' && question.options && (
              <div className="space-y-4">
                <div className="space-y-2">
                  {question.options.map((option) => (
                    <label
                      key={option.value}
                      className="flex items-center gap-3 px-4 py-3 rounded-lg border border-cosmic-border bg-cosmic-surface/50 cursor-pointer hover:bg-cosmic-nebula/10 transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={multiSelectValues.has(option.value)}
                        onChange={() => handleMultiSelectToggle(option.value)}
                        className="w-4 h-4 rounded border-cosmic-border bg-cosmic-void text-cosmic-nebula focus:ring-cosmic-nebula/50"
                      />
                      <span className="text-white">{option.label}</span>
                    </label>
                  ))}
                </div>
                <button
                  onClick={handleMultiSelectSubmit}
                  disabled={multiSelectValues.size === 0}
                  className="w-full px-4 py-3 rounded-lg bg-cosmic-nebula text-white font-medium hover:bg-cosmic-nebula/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Submit ({multiSelectValues.size} selected)
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
